import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Input } from '@/components/Input';
import { CreateUserModal } from '@/components/users/CreateUserModal';
import { BulkOperations, UserSelectionCheckbox } from '@/components/users/BulkOperations';
import { UserRole, getUserRoleFromString } from '@/types/api';
import type { User, UserQueryParams, Company } from '@/types/api';
import { useQuery } from '@tanstack/react-query';
import { userService } from '@/services/userService';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

/**
 * Convert backend UserInfo to frontend User format
 */
interface BackendUserInfo {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  role: string;
  isActive: boolean;
  companyId?: string;
  companyName?: string;
  timeZone?: string;
  language?: string;
  avatarUrl?: string;
  department?: string;
  jobTitle?: string;
  phone?: string;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
  ticketCount: number;
}

const convertBackendUserToFrontendUser = (backendUser: BackendUserInfo): User => {
  return {
    id: backendUser.id,
    email: backendUser.email,
    firstName: backendUser.firstName,
    lastName: backendUser.lastName,
    fullName: backendUser.fullName || `${backendUser.firstName} ${backendUser.lastName}`,
    role: getUserRoleFromString(backendUser.role),
    isActive: backendUser.isActive,
    companyId: backendUser.companyId,
    company: backendUser.companyName ? { 
      id: backendUser.companyId || '', 
      name: backendUser.companyName,
      createdAt: '',
      updatedAt: ''
    } : undefined,
    profileImageUrl: backendUser.avatarUrl,
    lastLoginAt: backendUser.lastLoginAt,
    createdAt: backendUser.createdAt,
    updatedAt: backendUser.updatedAt,
    department: backendUser.department,
    jobTitle: backendUser.jobTitle,
    phoneNumber: backendUser.phone,
    timezone: backendUser.timeZone,
    ticketCount: backendUser.ticketCount,
  };
};

/**
 * User Management Page Component
 * 
 * Admin-only page for managing all users in the system.
 * Allows creating, editing, and deactivating users.
 */
export function UserManagement() {
  useDocumentTitle('User Management | SupportDesk');
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(searchParams.get('create') === 'true');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
    show: boolean;
  }>({ message: '', type: 'info', show: false });


  // Build query parameters for API
  const queryParams = useMemo((): UserQueryParams => {
    const params: UserQueryParams = {
      page: 1,
      pageSize: 100, // Get more users for demo purposes
    };

    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }

    if (roleFilter !== 'all') {
      // Convert enum value to role name for backend
      const role = roleFilter as UserRole;
      params.role = [role];
    }

    if (statusFilter !== 'all') {
      params.isActive = statusFilter === 'active';
    }

    return params;
  }, [searchQuery, roleFilter, statusFilter]);

  // Fetch users data from API
  const {
    data: usersResponse,
    isLoading: loading,
    error,
    refetch
  } = useQuery({
    queryKey: ['users', queryParams],
    queryFn: () => userService.getUsers(queryParams)
  });

  // Extract users from response - handle both old and new backend response formats
  console.log('UserManagement: API Response:', usersResponse);
  let backendUsers: BackendUserInfo[] = [];
  
  if (usersResponse) {
    // Direct PaginatedUserResponse format: {users: [...], totalCount: ...}
    if (usersResponse.users) {
      backendUsers = usersResponse.users;
    }
    // New format: ApiResponse<PaginatedResponse<UserInfo>>
    else if (usersResponse.success && usersResponse.data?.data) {
      backendUsers = usersResponse.data.data;
    }
    // Old format: PaginatedUserResponse wrapped in ApiResponse
    else if (usersResponse.success && usersResponse.data?.users) {
      backendUsers = usersResponse.data.users;
    }
  }
  
  // Convert backend UserInfo to frontend User format
  const allUsers: User[] = backendUsers.map((backendUser: BackendUserInfo) => 
    convertBackendUserToFrontendUser(backendUser)
  );
  console.log('UserManagement: Extracted and converted users:', allUsers);

  // Get selected users
  const selectedUsers = allUsers.filter(user => selectedUserIds.includes(user.id));

  // Use the users from API response (filtering is done server-side)
  const filteredUsers = allUsers;

  const getRoleBadge = (role: UserRole) => {
    switch (role) {
      case UserRole.Admin:
        return <Badge variant="error">Administrator</Badge>;
      case UserRole.Agent:
        return <Badge variant="info">Support Agent</Badge>;
      case UserRole.EndUser:
        return <Badge variant="success">End User</Badge>;
      default:
        return <Badge variant="secondary">{role}</Badge>;
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <Badge variant="success" size="sm">Active</Badge>
    ) : (
      <Badge variant="secondary" size="sm">Inactive</Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleExportCSV = async () => {
    if (exportLoading) return;

    try {
      setExportLoading(true);
      showToast('Preparing user export...', 'info');

      // Build export parameters with current filters
      const exportParams: UserQueryParams = {};
      
      if (searchQuery.trim()) {
        exportParams.search = searchQuery.trim();
      }

      if (roleFilter !== 'all') {
        const role = roleFilter as UserRole;
        exportParams.role = [role];
      }

      if (statusFilter !== 'all') {
        exportParams.isActive = statusFilter === 'active';
      }

      // Get CSV blob from the API
      const blob = await userService.exportUsers(exportParams);

      // Create download link with timestamp in filename
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users-export-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      // Show success message with file info
      const fileSize = (blob.size / 1024).toFixed(1);
      showToast(`Users exported successfully to CSV (${fileSize} KB)`, 'success');
    } catch (error) {
      console.error('Error exporting users:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      showToast(`Export failed: ${errorMessage}`, 'error');
    } finally {
      setExportLoading(false);
    }
  };

  const handleUserAction = async (userId: string, action: 'activate' | 'deactivate' | 'delete') => {
    if (actionLoading) return; // Prevent multiple simultaneous actions

    try {
      setActionLoading(userId);

      switch (action) {
        case 'activate':
          await userService.activateUser(userId);
          break;
        case 'deactivate':
          await userService.deactivateUser(userId);
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            await userService.deleteUser(userId);
          } else {
            return;
          }
          break;
      }

      // Refresh the user list after successful action
      refetch();
      
    } catch (error) {
      console.error(`Failed to ${action} user:`, error);
      alert(`Failed to ${action} user. Please try again.`);
    } finally {
      setActionLoading(null);
    }
  };

  // Load companies for user creation
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const response = await userService.getCompanies();
        if (response.success) {
          setCompanies(response.data);
        }
      } catch (error) {
        // Gracefully handle missing companies endpoint
        // In production, companies would be loaded from the API
        console.info('Companies API not available, using empty list');
        setCompanies([]);
      }
    };
    loadCompanies();
  }, []);

  // Toast notification handler
  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type, show: true });
    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 5000);
  }, []);

  // Handle user creation success
  const handleUserCreated = useCallback((newUser: User) => {
    showToast(`User ${newUser.fullName} has been created successfully.`, 'success');
    refetch(); // Refresh the user list
  }, [refetch, showToast]);

  // Handle bulk operation completion
  const handleBulkActionComplete = useCallback((action: string, count: number) => {
    const actionMap = {
      activate: 'activated',
      deactivate: 'deactivated',
      delete: 'deleted',
      export: 'exported',
      changeRole: 'role changed for'
    };
    const actionText = actionMap[action as keyof typeof actionMap] || action;
    showToast(`Successfully ${actionText} ${count} user${count > 1 ? 's' : ''}.`, 'success');
    refetch(); // Refresh the user list
  }, [refetch, showToast]);

  // Handle bulk operation error
  const handleBulkActionError = useCallback((error: string) => {
    showToast(error, 'error');
  }, [showToast]);

  // Handle user selection change
  const handleSelectionChange = useCallback((userIds: string[]) => {
    setSelectedUserIds(userIds);
  }, []);

  // Check if current user is admin
  if (user?.role !== UserRole.Admin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-4">You don't have permission to access user management.</p>
          <Button onClick={() => window.history.back()}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white shadow">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="py-6 md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl" data-testid="users-title">
                User Management
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {loading ? 'Loading...' : `${filteredUsers.length} user${filteredUsers.length !== 1 ? 's' : ''} found`}
              </p>
            </div>
            <div className="mt-6 flex items-center space-x-3 md:mt-0 md:ml-4">
              <Button 
                variant="outline"
                onClick={handleExportCSV}
                disabled={exportLoading || filteredUsers.length === 0}
                data-testid="export-csv-button"
                className="h-10 inline-flex items-center justify-center"
                title={filteredUsers.length === 0 ? 'No users to export' : `Export ${filteredUsers.length} user(s) to CSV`}
              >
                {exportLoading ? (
                  <>
                    <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Exporting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                    </svg>
                    Export CSV {filteredUsers.length > 0 ? `(${filteredUsers.length})` : ''}
                  </>
                )}
              </Button>
              <Button 
                variant="primary"
                onClick={() => setShowCreateModal(true)}
                data-testid="create-user-button"
                className="h-10 inline-flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add User
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 text-gray-400">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {loading ? '...' : (usersResponse?.success ? (usersResponse.data?.totalCount || allUsers.length) : allUsers.length)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 text-blue-400">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Agents</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {loading ? '...' : allUsers.filter((u: User) => u.role === UserRole.Agent).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 text-green-400">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">End Users</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {loading ? '...' : allUsers.filter((u: User) => u.role === UserRole.EndUser).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 text-green-500">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Users</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {loading ? '...' : allUsers.filter((u: User) => u.isActive).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 max-w-sm w-full`}>
          <div className={`rounded-md p-4 ${
            toast.type === 'success' ? 'bg-green-50 border border-green-200' :
            toast.type === 'error' ? 'bg-red-50 border border-red-200' :
            'bg-blue-50 border border-blue-200'
          }`}>
            <div className="flex">
              <div className="flex-shrink-0">
                {toast.type === 'success' && (
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
                {toast.type === 'error' && (
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                {toast.type === 'info' && (
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  toast.type === 'success' ? 'text-green-800' :
                  toast.type === 'error' ? 'text-red-800' :
                  'text-blue-800'
                }`}>
                  {toast.message}
                </p>
              </div>
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button
                    type="button"
                    className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      toast.type === 'success' ? 'text-green-500 hover:bg-green-100 focus:ring-green-600' :
                      toast.type === 'error' ? 'text-red-500 hover:bg-red-100 focus:ring-red-600' :
                      'text-blue-500 hover:bg-blue-100 focus:ring-blue-600'
                    }`}
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                  >
                    <span className="sr-only">Dismiss</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Search Users
              </label>
              <Input
                id="search"
                type="text"
                placeholder="Search by name, email, or company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="user-search"
              />
            </div>

            {/* Role Filter */}
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                id="role"
                className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-sm"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                data-testid="role-filter"
              >
                <option value="all">All Roles</option>
                <option value={UserRole.Admin}>Administrator</option>
                <option value={UserRole.Agent}>Support Agent</option>
                <option value={UserRole.EndUser}>End User</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="status"
                className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>
      </Card>

      {/* Bulk Operations */}
      <BulkOperations
        selectedUsers={selectedUsers}
        allUsers={filteredUsers}
        onSelectionChange={handleSelectionChange}
        onBulkActionComplete={handleBulkActionComplete}
        onError={handleBulkActionError}
        isUsingMockData={false}
      />

      {/* Loading state */}
      {loading && (
        <Card>
          <div className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-300 rounded w-1/4"></div>
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-16 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Error state */}
      {error && (
        <Card>
          <div className="p-6">
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error loading users</h3>
                  <p className="mt-1 text-sm text-red-700">{error?.message || 'Unknown error occurred'}</p>
                  <div className="mt-4">
                    <Button 
                      onClick={() => refetch()} 
                      variant="outline"
                      size="sm"
                    >
                      Try Again
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Users Table */}
      {!loading && (
        <Card>
          <div className="p-6">
            {filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No users found</h3>
              <p className="mt-2 text-sm text-gray-500">
                {searchQuery ? 'Try adjusting your search criteria.' : 'Get started by adding a new user.'}
              </p>
              <div className="mt-6">
                <Link to="/admin/users/new">
                  <Button variant="primary">Add New User</Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200" data-testid="users-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.length === filteredUsers.length && filteredUsers.length > 0}
                        ref={input => {
                          if (input) {
                            input.indeterminate = selectedUserIds.length > 0 && selectedUserIds.length < filteredUsers.length;
                          }
                        }}
                        onChange={() => {
                          if (selectedUserIds.length === filteredUsers.length) {
                            setSelectedUserIds([]);
                          } else {
                            setSelectedUserIds(filteredUsers.map(user => user.id));
                          }
                        }}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        aria-label="Select all users"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tickets
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((userItem) => (
                    <tr key={userItem.id} className="hover:bg-gray-50" data-testid="user-row">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <UserSelectionCheckbox
                          user={userItem}
                          isSelected={selectedUserIds.includes(userItem.id)}
                          onSelectionChange={(userId, checked) => {
                            if (checked) {
                              setSelectedUserIds(prev => [...prev, userId]);
                            } else {
                              setSelectedUserIds(prev => prev.filter(id => id !== userId));
                            }
                          }}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-700">
                                {userItem.firstName[0]}{userItem.lastName[0]}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              <Link
                                to={`/admin/users/${userItem.id}/edit`}
                                className="text-gray-900 hover:text-primary-600 transition-colors duration-200 cursor-pointer"
                                title={`Edit ${userItem.fullName}`}
                              >
                                {userItem.fullName}
                              </Link>
                            </div>
                            <div className="text-sm text-gray-500">{userItem.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getRoleBadge(userItem.role)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {userItem.company?.name || 'No Company'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(userItem.isActive)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {userItem.lastLoginAt ? formatDate(userItem.lastLoginAt) : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {userItem.ticketCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <Link
                            to={`/admin/users/${userItem.id}/edit`}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            Edit
                          </Link>
                          {userItem.isActive ? (
                            <button
                              onClick={() => handleUserAction(userItem.id, 'deactivate')}
                              disabled={actionLoading === userItem.id}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {actionLoading === userItem.id ? 'Loading...' : 'Deactivate'}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleUserAction(userItem.id, 'activate')}
                              disabled={actionLoading === userItem.id}
                              className="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {actionLoading === userItem.id ? 'Loading...' : 'Activate'}
                            </button>
                          )}
                          <button
                            onClick={() => handleUserAction(userItem.id, 'delete')}
                            disabled={actionLoading === userItem.id}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {actionLoading === userItem.id ? 'Loading...' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </div>
        </Card>
      )}

      {/* Pagination - Mock for demonstration */}
      {filteredUsers.length > 0 && (
        <Card>
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing 1 to {filteredUsers.length} of {filteredUsers.length} users
              </div>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" disabled>
                  Previous
                </Button>
                <span className="px-3 py-1.5 text-xs font-medium bg-primary-50 text-primary-600 rounded min-h-[32px] flex items-center">
                  1
                </span>
                <Button variant="outline" size="sm" disabled>
                  Next
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          // Clear the 'create' parameter from URL when modal closes
          if (searchParams.get('create') === 'true') {
            searchParams.delete('create');
            setSearchParams(searchParams);
          }
        }}
        onSuccess={handleUserCreated}
        companies={companies}
      />
    </div>
  );
}